import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { GradientNumbers } from '@webapp/common/components/gradient-numbers';
import { NumbersWithBorders } from '@webapp/common/components/numbers-with-borders';
import { Rating } from '@webapp/common/components/rating';
import { getTrackColors, Slider } from '@webapp/common/components/slider';
import { CssPageBlock, CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './matrix-rating.css';

export const MatrixRatingRow: FC<{
    questionId: number;
    answer: QuestionAnswerActor;
    style: React.CSSProperties;
    color: any;
    minAmount: number;
    amount: number;
    size: number;
    matrixType: string;
    matrixNumbersColor: Array<string>;
    cantRate: boolean;
    cantRateText: string;
}> = ({
    answer,
    matrixNumbersColor,
    matrixType,
    minAmount,
    amount,
    size,
    style,
    questionId,
    cantRate,
    cantRateText,
}) => {
    const itemsCount = amount + (minAmount === 0 ? 1 : 0);
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const { listStyle } = useTextStyle();
    const {
        response: responseActor,
        valid,
        value,
        hidden,
        hiddenByAnswersHide,
        forwardParent,
    } = useSelector(answer, (a) => a.context);
    const response = useSelector(responseActor, (r) => r.context);

    const cantRateChecked = response.value === CANTRATE_ID;
    const [forwardParentActor, setForwardParentActor] = useState<undefined | QuestionAnswerActor>();
    const forwardParentContext = useSelector(forwardParentActor, (s) => s?.context);

    const forwardParentValue = useSelector(forwardParentContext?.response, (s) => s?.context.extra);
    const questions = useStateSelector((s) => s.questions);

    useEffect(() => {
        if (!forwardParent && !forwardParentActor) return;

        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === forwardParent;
            });
            if (founded) {
                setForwardParentActor(founded);
            }
        });
    }, [questions, forwardParent]);

    const onChange = useCallback(
        (value: number): void => {
            responseActor.send({ type: 'CHANGE', value, questionId });
        },
        [responseActor, questionId],
    );

    const handleCantRate = useCallback(
        (checked: boolean) => {
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId });
        },
        [responseActor, questionId],
    );

    const renderInput = () => {
        const value = cantRateChecked ? undefined : (response.value as number);

        if (matrixType === 'numbers-with-borders') {
            return (
                <NumbersWithBorders
                    colors={matrixNumbersColor}
                    itemsCount={itemsCount}
                    startsWith={minAmount}
                    selectedValue={value}
                    onClick={onChange}
                    className={css.matrixNumber}
                />
            );
        }
        if (matrixType === 'slider') {
            return (
                <Slider
                    min={minAmount}
                    max={amount}
                    value={typeof value === 'number' ? value : null}
                    withoutControl
                    rangeClassName={css.rangeSlider}
                    labelsBottom
                    trackBgImage={`linear-gradient(90deg, ${getTrackColors(matrixNumbersColor)})`}
                    onChange={onChange}
                />
            );
        }
        // Сделано доп условие для обратной совместимости
        if (matrixType === 'numbers') {
            return (
                <GradientNumbers
                    size='small'
                    colors={matrixNumbersColor}
                    itemsCount={itemsCount}
                    startsWith={minAmount}
                    selectedValue={value}
                    onClick={onChange}
                />
            );
        }

        return (
            <Rating
                key={String(cantRateChecked)}
                color={matrixNumbersColor?.[0]}
                count={itemsCount}
                value={value}
                size={size}
                onChange={onChange}
            />
        );
    };

    if (hidden === true || hiddenByAnswersHide === true) return null;

    return (
        <>
            <div
                className={cn(CssPageBlock.ANSWER, css.answer, {
                    [css.answer_invalid]: valid === false,
                })}
            >
                <div
                    className={cn(CssUiComponent.LABEL, css.label)}
                    style={style}
                    {...setHtml(pipingAnswersReplacer(forwardParentValue ? forwardParentValue.toString() : value[0]))}
                />
                <div className={css.field}>{renderInput()}</div>
            </div>
            {cantRate && (
                <div style={{ marginTop: '10px', gridColumn: '1/-1', marginBottom: `calc(${listStyle.rowGap} * 2)` }}>
                    <Checkbox checked={cantRateChecked} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </>
    );
};
